import { useState, useEffect, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
  ArrowLeft,
  FileText,
  MessageSquare,
  BarChart3,
  FileSpreadsheet,
  Grid3X3,
  CheckSquare,
  Edit3,
  Save,
  X,
  AlertCircle,
  Calendar,
  User,
  Video,
} from 'lucide-react'
import * as api from '../services/api'
import type { Project, UpdateProjectRequest } from '../types'
import TeamsImportModal from '../components/TeamsImportModal'

const quickLinks = [
  {
    name: 'Documents',
    href: 'documents',
    icon: FileText,
    color: 'blue',
    description: 'Upload and manage project documents',
  },
  {
    name: 'Teams Transcripts',
    href: null, // Opens modal instead of navigating
    icon: Video,
    color: 'violet',
    description: 'Import transcripts from Teams meetings',
    isModal: true,
  },
  {
    name: 'Chat',
    href: 'chat',
    icon: MessageSquare,
    color: 'cyan',
    description: 'Ask questions about your documents',
  },
  {
    name: 'Outputs',
    href: 'outputs',
    icon: FileSpreadsheet,
    color: 'amber',
    description: 'Generate CSV outputs for Scrum artifacts',
  },
  {
    name: 'Smartsheets',
    href: 'smartsheets',
    icon: Grid3X3,
    color: 'green',
    description: 'Connect and sync with Smartsheets',
  },
  {
    name: 'Analysis',
    href: 'analysis',
    icon: BarChart3,
    color: 'purple',
    description: 'Run gap analysis and generate questions',
  },
]

function getStatusBadge(status: string) {
  switch (status) {
    case 'active':
      return <span className="badge-green">Active</span>
    case 'completed':
      return <span className="badge-blue">Completed</span>
    case 'on_hold':
      return <span className="badge-yellow">On Hold</span>
    case 'archived':
      return <span className="badge-gray">Archived</span>
    default:
      return <span className="badge-gray">{status}</span>
  }
}

export default function ProjectDetail() {
  const { projectId } = useParams<{ projectId: string }>()
  const [project, setProject] = useState<Project | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [editForm, setEditForm] = useState<UpdateProjectRequest>({})
  const [showTeamsModal, setShowTeamsModal] = useState(false)

  const fetchProject = useCallback(async () => {
    if (!projectId) return
    setLoading(true)
    setError(null)
    try {
      const response = await api.getProject(projectId)
      setProject(response.data)
      setEditForm({
        name: response.data.name,
        customer_name: response.data.customer_name,
        description: response.data.description,
        status: response.data.status,
      })
    } catch (err) {
      console.error('Failed to fetch project:', err)
      setError('Failed to load project')
    } finally {
      setLoading(false)
    }
  }, [projectId])

  useEffect(() => {
    fetchProject()
  }, [fetchProject])

  const handleSave = async () => {
    if (!projectId) return
    setSaving(true)
    setError(null)
    try {
      await api.updateProject(projectId, editForm)
      await fetchProject()
      setEditing(false)
    } catch (err) {
      console.error('Failed to update project:', err)
      setError('Failed to update project')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <div className="skeleton w-10 h-10 rounded-lg" />
          <div>
            <div className="skeleton h-6 w-48 mb-2" />
            <div className="skeleton h-4 w-32" />
          </div>
        </div>
        <div className="card">
          <div className="skeleton h-20 w-full" />
        </div>
      </div>
    )
  }

  if (error || !project) {
    return (
      <div className="space-y-6">
        <Link to="/projects" className="btn-ghost inline-flex">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Projects
        </Link>
        <div className="alert-error">
          <AlertCircle className="w-5 h-5" />
          {error || 'Project not found'}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <Link to="/projects" className="btn-ghost btn-icon">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            {editing ? (
              <input
                type="text"
                value={editForm.name || ''}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                className="input text-xl font-bold"
              />
            ) : (
              <h1 className="text-2xl font-bold text-slate-100">{project.name}</h1>
            )}
            {editing ? (
              <input
                type="text"
                value={editForm.customer_name || ''}
                onChange={(e) => setEditForm({ ...editForm, customer_name: e.target.value })}
                className="input mt-2"
                placeholder="Customer name"
              />
            ) : (
              <p className="text-slate-400 mt-1">{project.customer_name}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {editing ? (
            <>
              <button onClick={() => setEditing(false)} className="btn-ghost" disabled={saving}>
                <X className="w-4 h-4" />
                Cancel
              </button>
              <button onClick={handleSave} className="btn-primary" disabled={saving}>
                {saving ? (
                  <>
                    <div className="spinner" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save
                  </>
                )}
              </button>
            </>
          ) : (
            <button onClick={() => setEditing(true)} className="btn-secondary">
              <Edit3 className="w-4 h-4" />
              Edit
            </button>
          )}
        </div>
      </div>

      {/* Project Info Card */}
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div>
            <p className="text-sm text-slate-500 mb-1">Status</p>
            {editing ? (
              <select
                value={editForm.status || 'active'}
                onChange={(e) =>
                  setEditForm({ ...editForm, status: e.target.value as Project['status'] })
                }
                className="select"
              >
                <option value="active">Active</option>
                <option value="on_hold">On Hold</option>
                <option value="completed">Completed</option>
                <option value="archived">Archived</option>
              </select>
            ) : (
              getStatusBadge(project.status)
            )}
          </div>
          <div>
            <p className="text-sm text-slate-500 mb-1">Documents</p>
            <p className="text-slate-200 font-medium flex items-center gap-2">
              <FileText className="w-4 h-4 text-blue-400" />
              {project.document_count || 0} files
            </p>
          </div>
          <div>
            <p className="text-sm text-slate-500 mb-1">Created</p>
            <p className="text-slate-200 font-medium flex items-center gap-2">
              <Calendar className="w-4 h-4 text-slate-400" />
              {new Date(project.created_at).toLocaleDateString()}
            </p>
          </div>
          <div>
            <p className="text-sm text-slate-500 mb-1">Project Manager</p>
            <p className="text-slate-200 font-medium flex items-center gap-2">
              <User className="w-4 h-4 text-slate-400" />
              {project.project_manager || 'Not assigned'}
            </p>
          </div>
        </div>
        {(project.description || editing) && (
          <div className="mt-6 pt-6 border-t border-slate-700/50">
            <p className="text-sm text-slate-500 mb-2">Description</p>
            {editing ? (
              <textarea
                value={editForm.description || ''}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                className="textarea"
                rows={3}
                placeholder="Project description..."
              />
            ) : (
              <p className="text-slate-300">{project.description || 'No description'}</p>
            )}
          </div>
        )}
      </div>

      {/* Quick Links */}
      <div>
        <h2 className="text-lg font-semibold text-slate-100 mb-4">Project Tools</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {quickLinks.map((link) => {
            const Icon = link.icon

            // Handle modal-based links differently
            if (link.isModal) {
              return (
                <button
                  key={link.name}
                  onClick={() => setShowTeamsModal(true)}
                  className="card glass-panel-hover group text-left"
                >
                  <div className="flex items-start gap-4">
                    <div
                      className={`w-12 h-12 rounded-xl flex items-center justify-center bg-${link.color}-500/20`}
                    >
                      <Icon className={`w-6 h-6 text-${link.color}-400`} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-100 group-hover:text-violet-400 transition-colors">
                        {link.name}
                      </h3>
                      <p className="text-sm text-slate-400 mt-1">{link.description}</p>
                    </div>
                  </div>
                </button>
              )
            }

            return (
              <Link
                key={link.href}
                to={`/projects/${projectId}/${link.href}`}
                className="card glass-panel-hover group"
              >
                <div className="flex items-start gap-4">
                  <div
                    className={`w-12 h-12 rounded-xl flex items-center justify-center bg-${link.color}-500/20`}
                  >
                    <Icon className={`w-6 h-6 text-${link.color}-400`} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-100 group-hover:text-blue-400 transition-colors">
                      {link.name}
                    </h3>
                    <p className="text-sm text-slate-400 mt-1">{link.description}</p>
                  </div>
                </div>
              </Link>
            )
          })}
        </div>
      </div>

      {/* Post-Import Tasks Card */}
      <div className="card bg-gradient-to-r from-purple-500/10 to-cyan-500/10 border-purple-500/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
              <CheckSquare className="w-6 h-6 text-purple-400" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-100">Post-Import Tasks</h3>
              <p className="text-sm text-slate-400 mt-1">
                Manual configuration tasks after Smartsheets import
              </p>
            </div>
          </div>
          <Link to={`/projects/${projectId}/smartsheets`} className="btn-ghost">
            View Tasks
          </Link>
        </div>
      </div>

      {/* Teams Import Modal */}
      {projectId && (
        <TeamsImportModal
          projectId={projectId}
          isOpen={showTeamsModal}
          onClose={() => setShowTeamsModal(false)}
          onImportSuccess={() => {
            // Optionally refresh project data or show success message
            fetchProject()
          }}
        />
      )}
    </div>
  )
}
